import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUser, FaCommentAlt, FaSpinner, FaShieldAlt } from 'react-icons/fa';
import { fetchWithAuth } from '../services/api';
import { 
  isDevelopmentEnvironment, 
  isTestSiteKey, 
  handleCaptchaToken, 
  getCaptchaErrorMessage,
  generateDummyToken
} from '../utils/captcha-utils';

const CommentModal = ({ isOpen, onClose, profileStringId, onCommentAdded }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', content: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  // CAPTCHA States
  const [captchaToken, setCaptchaToken] = useState('');
  const [isCaptchaLoaded, setIsCaptchaLoaded] = useState(false);
  const [captchaWidgetId, setCaptchaWidgetId] = useState(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [captchaSiteKey, setCaptchaSiteKey] = useState('1x00000000000000000000AA'); // fallback test key
  const captchaContainerRef = useRef(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({ name: '', content: '' });
      setErrors({});
      setCaptchaToken('');
      setCaptchaKey(prev => prev + 1);
    }
  }, [isOpen]);

  const handleCaptchaSuccess = (token) => {
    const processedToken = handleCaptchaToken(token, captchaSiteKey);
    setCaptchaToken(processedToken);
    setErrors(prev => ({ ...prev, captcha: '' }));
  };

  const handleCaptchaError = (errorCode) => {
    if (isDevelopmentEnvironment() && isTestSiteKey(captchaSiteKey)) {
      const dummyToken = generateDummyToken();
      setCaptchaToken(dummyToken);
      setErrors(prev => ({ ...prev, captcha: '' }));
      return;
    }
    
    setCaptchaToken('');
    const errorMessage = getCaptchaErrorMessage(errorCode, captchaSiteKey);
    setErrors(prev => ({ ...prev, captcha: errorMessage }));
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken('');
    setErrors(prev => ({ ...prev, captcha: 'CAPTCHA expired. Please solve it again.' }));
  };

  const initializeCaptcha = () => {
    if (!window.turnstile) {
      console.warn('Turnstile not loaded yet');
      return;
    }

    if (!captchaContainerRef.current) {
      console.warn('Captcha container ref not available');
      return;
    }

    if (captchaWidgetId !== null) {
      return;
    }

    if (!captchaSiteKey) {
      return;
    }

    const container = captchaContainerRef.current;
    
    // Clean up any existing widgets completely
    if (container.querySelector('[data-cf-turnstile]') || container.querySelector('iframe')) {
      container.innerHTML = '';
    }
    
    try {
      if (window.turnstile && window.turnstile.remove) {
        window.turnstile.remove();
      }
    } catch (e) {
      console.log('Could not call turnstile.remove():', e.message);
    }
    
    try {
      const widgetId = window.turnstile.render(container, {
        sitekey: captchaSiteKey,
        callback: handleCaptchaSuccess,
        'error-callback': handleCaptchaError,
        'expired-callback': handleCaptchaExpire,
        theme: 'light',
        size: 'normal',
        retry: 'auto',
        'refresh-expired': 'auto'
      });
      setCaptchaWidgetId(widgetId);
    } catch (error) {
      console.error('Error rendering CAPTCHA:', error);
      
      if (error.message && error.message.includes('already been rendered')) {
        container.innerHTML = '';
        setTimeout(() => {
          try {
            const retryWidgetId = window.turnstile.render(container, {
              sitekey: captchaSiteKey,
              callback: handleCaptchaSuccess,
              'error-callback': handleCaptchaError,
              'expired-callback': handleCaptchaExpire,
              theme: 'light',
              size: 'normal',
              retry: 'auto',
              'refresh-expired': 'auto'
            });
            setCaptchaWidgetId(retryWidgetId);
          } catch (retryError) {
            console.error('Failed to re-render CAPTCHA:', retryError);
            setErrors(prev => ({
              ...prev,
              captcha: 'Failed to initialize CAPTCHA. Please refresh the page.'
            }));
          }
        }, 100);
      } else {
        setErrors(prev => ({
          ...prev,
          captcha: 'Failed to initialize CAPTCHA. Please refresh the page.'
        }));
      }
    }
  };

  const resetCaptcha = () => {
    if (captchaWidgetId !== null && window.turnstile) {
      try {
        window.turnstile.reset(captchaWidgetId);
        setCaptchaToken('');
        return;
      } catch (error) {
        console.warn('Could not reset CAPTCHA widget, will reinitialize:', error);
      }
    }
    
    if (captchaContainerRef.current) {
      captchaContainerRef.current.innerHTML = '';
    }
    
    setCaptchaWidgetId(null);
    setCaptchaToken('');
    setCaptchaKey(prev => prev + 1);
  };

  // Fetch captcha config when modal is open
  useEffect(() => {
    if (!isOpen) return;

    const fetchCaptchaConfig = async () => {
      try {
        const config = await fetchWithAuth('/auth/captcha-config', { isPublic: true });
        setCaptchaSiteKey(config.siteKey);
      } catch (error) {
        console.error('Failed to fetch CAPTCHA config:', error);
      }
    };
    
    fetchCaptchaConfig();
  }, [isOpen]);

  // Load turnstile script dynamically when modal is open
  useEffect(() => {
    if (!isOpen) return;
    
    let isEffectActive = true;
    
    if (window.turnstile) {
      if (isEffectActive) {
        setIsCaptchaLoaded(true);
      }
      return;
    }

    const existingScript = document.querySelector('script[src*="turnstile"]');
    if (existingScript) {
      const handleExistingLoad = () => {
        if (isEffectActive) {
          setIsCaptchaLoaded(true);
        }
      };
      existingScript.addEventListener('load', handleExistingLoad);
      return () => {
        isEffectActive = false;
        existingScript.removeEventListener('load', handleExistingLoad);
      };
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.id = 'turnstile-script';
    
    const handleLoad = () => {
      if (isEffectActive) {
        setIsCaptchaLoaded(true);
      }
    };
    
    const handleError = () => {
      if (isEffectActive) {
        setErrors(prev => ({
          ...prev,
          captcha: 'Failed to load CAPTCHA. Please refresh the page.'
        }));
      }
    };
    
    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
    document.head.appendChild(script);

    return () => {
      isEffectActive = false;
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
      
      const ourScript = document.getElementById('turnstile-script');
      if (ourScript) {
        ourScript.remove();
      }
    };
  }, [isOpen]);

  // Render widget when captcha is loaded or refreshed
  useEffect(() => {
    if (!isOpen) return;

    if (isCaptchaLoaded && window.turnstile && captchaContainerRef.current && captchaSiteKey) {
      if (captchaWidgetId !== null) {
        try {
          window.turnstile.remove(captchaWidgetId);
        } catch (e) {
          console.log('Could not remove widget:', e.message);
        }
        setCaptchaWidgetId(null);
      }
      
      const timer = setTimeout(() => {
        if (captchaWidgetId === null) {
          initializeCaptcha();
        }
      }, 300);
      
      return () => {
        clearTimeout(timer);
        if (captchaWidgetId !== null && window.turnstile) {
          try {
            window.turnstile.remove(captchaWidgetId);
          } catch (e) {
            console.log('Could not remove widget on unmount:', e.message);
          }
        }
      };
    }
  }, [isOpen, isCaptchaLoaded, captchaKey, captchaSiteKey]);

  // Close modal when hitting ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = t('profile.commentValidationNameRequired', 'Please enter your name.');
    }
    if (!formData.content.trim()) {
      newErrors.content = t('profile.commentValidationContentRequired', 'Please write a comment.');
    }
    if (!captchaToken) {
      newErrors.captcha = t('profile.commentValidationCaptchaRequired', 'Please complete the CAPTCHA verification.');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (validateForm()) {
      setIsLoading(true);
      const requestData = {
        name: formData.name.trim(),
        content: formData.content.trim(),
        captchaToken: captchaToken
      };

      try {
        const response = await fetchWithAuth(`/profiles/${profileStringId}/comments`, {
          method: 'POST',
          body: requestData,
          isPublic: true
        });

        if (response) {
          onCommentAdded(response);
          onClose();
        }
      } catch (err) {
        console.error('Failed to submit comment:', err);
        setErrors(prev => ({
          ...prev,
          submit: err.message || 'An error occurred. Please try again.'
        }));
        resetCaptcha();
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-lg w-full overflow-hidden transform transition-all z-10 font-sans">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-4 flex items-center justify-between text-white">
          <div className="flex items-center space-x-2">
            <FaCommentAlt className="text-blue-400" />
            <h3 className="text-lg font-medium tracking-wide">
              {t('profile.writeCommentModalTitle', 'Share a Memory or Condolence')}
            </h3>
          </div>
          <button 
            onClick={onClose}
            className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Submit/General Error */}
          {errors.submit && (
            <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-200">
              {errors.submit}
            </div>
          )}

          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('profile.commentFormName', 'Your Name')}
            </label>
            <div className="relative rounded-lg shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaUser className="h-4 w-4 text-gray-400" />
              </div>
              <input
                id="name"
                name="name"
                type="text"
                required
                disabled={isLoading}
                value={formData.name}
                onChange={handleChange}
                className={`appearance-none block w-full pl-10 pr-3 py-2.5 border ${
                  errors.name ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-slate-500 focus:border-slate-500'
                } rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 transition-colors text-gray-900 text-sm`}
                placeholder="e.g. John Doe"
              />
            </div>
            {errors.name && (
              <p className="mt-1 text-xs text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Comment Field */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('profile.commentFormContent', 'Your Memory / Condolence')}
            </label>
            <textarea
              id="content"
              name="content"
              required
              rows="4"
              disabled={isLoading}
              value={formData.content}
              onChange={handleChange}
              className={`appearance-none block w-full px-3 py-2.5 border ${
                errors.content ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-slate-500 focus:border-slate-500'
              } rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 transition-colors text-gray-900 text-sm resize-none`}
              placeholder="Write your condolence or memory here..."
            />
            {errors.content && (
              <p className="mt-1 text-xs text-red-600">{errors.content}</p>
            )}
          </div>

          {/* Captcha Section */}
          <div className="pt-2 flex flex-col items-center border-t border-gray-100">
            <div className="flex items-center space-x-2 mb-3">
              <FaShieldAlt className="h-4 w-4 text-gray-500" />
              <span className="text-xs font-medium text-gray-600">
                Security Verification
              </span>
            </div>
            
            <div 
              key={captchaKey}
              ref={captchaContainerRef}
              className="min-h-[78px] flex items-center justify-center w-full max-w-[320px] mx-auto overflow-hidden"
            />
            
            {errors.captcha && (
              <p className="mt-2 text-xs text-red-600 text-center max-w-md">{errors.captcha}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="pt-4 border-t border-gray-100 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
            >
              {t('profile.commentFormCancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-slate-900 border border-transparent rounded-lg hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <FaSpinner className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
                  {t('profileCreator.buttons.submitting', 'Submitting...')}
                </>
              ) : (
                t('profile.commentFormSubmit', 'Post Memory')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommentModal;
